require('flickity-fullscreen');
import React, { useState, useRef, useEffect } from 'react';
import Flickity from 'react-flickity-component';
import { isEmpty, isNil, map } from 'lodash';
import Globalizer from '../../../globalizer';
import common from '../../../common/common';
import translateService from '../../../common/translateService';
import {
    userVoteYes,
    userVoteNo,
    reportAbuse,
} from '../../../services/reviews/reviewsService';
import {
    toggleNextElementVisibility,
    handleAbuseTypeChange,
} from '../../../services/product/productInteraction';
import ReviewStars from './ReviewStars.jsx';
import ProductFullScreenHandler from '../products/ProductFullScreenHandler.jsx';
import deferredImagesLoader from '../../../deferredImagesLoader';

const ProductReview = ({ review, isAuthenticated, isMobile, slug }) => {
    Globalizer.init();
    const reviewRef = useRef(null);
    const flktyHtmlRef = useRef(null);
    const reportAbuseFormRef = useRef(null);
    const [reviewState, setReviewState] = useState(review);
    const [flktyRef, setFlktyRef] = useState();
    const [abuseType, setAbuseType] = useState('');
    const [fullScreenActive, setFullScreenActive] = useState(false);
    const [indexZoom, setIndexZoom] = useState(0);

    useEffect(() => {
        setReviewState(review);

        deferredImagesLoader.observeReviewGallery({
            element: reviewRef.current,
            cb: (imageToLazyLoad) => {
                if (imageToLazyLoad) {
                    setReviewState({ ...review, renderImages: true });
                }
            },
        });
    }, [review]);

    const handleRadioButtonClick = (e) => {
        handleAbuseTypeChange(e, reportAbuseFormRef);
        const type = e.currentTarget.value;
        setAbuseType(type);
    };

    async function handleReviewIsUseful() {
        if (!isAuthenticated) {
            window.location = common.getSiteUrl(
                `${
                    window.$pathBase
                }Account/LogOn?returnUrl=${encodeURIComponent(
                    window.$pathBase + slug + window.location.search
                )}`
            );
            return;
        }
        if (isAuthenticated && !reviewState.userAlreadyVotedForUseful) {
            const result = await userVoteYes(reviewState.id, slug);
            if (result.isSuccess) {
                setReviewState((prevState) => {
                    const uselessCount = prevState.userAlreadyVotedForUseless
                        ? prevState.uselessCount - 1
                        : prevState.uselessCount;
                    const usefulCount = prevState.usefulCount + 1;

                    return {
                        ...prevState,
                        userAlreadyVotedForUseful: true,
                        userAlreadyVotedForUseless: false,
                        usefulCount: usefulCount,
                        uselessCount: uselessCount,
                    };
                });
            }
        }
    }

    async function handleReviewIsUseless() {
        if (!isAuthenticated) {
            window.location = common.getSiteUrl(
                `${
                    window.$pathBase
                }Account/LogOn?returnUrl=${encodeURIComponent(
                    window.$pathBase + slug + window.location.search
                )}`
            );
            return;
        }
        if (isAuthenticated && !reviewState.userAlreadyVotedForUseless) {
            const result = await userVoteNo(reviewState.id, slug);
            if (result.isSuccess) {
                setReviewState((prevState) => {
                    const usefulCount = prevState.userAlreadyVotedForUseful
                        ? prevState.usefulCount - 1
                        : prevState.usefulCount;
                    const uselessCount = prevState.uselessCount + 1;

                    return {
                        ...prevState,
                        userAlreadyVotedForUseful: false,
                        userAlreadyVotedForUseless: true,
                        usefulCount: usefulCount,
                        uselessCount: uselessCount,
                    };
                });
            }
        }
    }

    async function handleSendReportAbuse() {
        const result = await reportAbuse(reviewState.id, slug, abuseType);

        if (result.isSuccess) {
            reportAbuseFormRef.current.classList.remove('open');
        }
    }

    const handleImageClick = (e, index) => {
        setFullScreenActive(!fullScreenActive);
        setIndexZoom(index);
    };

    const images = map(reviewState.images, (image, idx) => (
        <div className='reviews-cell' key={idx}>
            <img onClick={(e) => handleImageClick(e, idx)} src={image} />
        </div>
    ));

    if (flktyRef) {
        flktyRef.on('fullscreenChange', (isFullscreen) => {
            const body = document.querySelector('body');

            if (isFullscreen) {
                body.classList.add('no-scroll');
            } else {
                body.classList.remove('no-scroll');
            }
        });
    }

    return (
        <div className='prd-review' ref={reviewRef}>
            <div className='prd-review-content'>
                <h3 className='prd-review-title'>{reviewState.title}</h3>
                <div className='prd-review-heading'>
                    <div className='prd-review-rating'>
                        <p>
                            <span>
                                {translateService.get(`reviews.ratingLabel`)}
                            </span>{' '}
                            <ReviewStars
                                average={reviewState.rating}
                                maxRating={5}
                            />
                        </p>
                        <p>
                            <span>
                                {translateService.get(
                                    `reviews.qualityPriceRatio`
                                )}
                            </span>{' '}
                            <ReviewStars
                                average={reviewState.qualityPriceRatio}
                                maxRating={5}
                            />
                        </p>
                    </div>
                    <p>
                        {`${
                            reviewState.user.nickname
                        } | ${Globalizer.formatDateLong(
                            reviewState.creationDate
                        )}`}
                    </p>
                </div>
                <p dangerouslySetInnerHTML={{ __html: reviewState.review }} />
                {!isEmpty(reviewState.images) &&
                    reviewState.renderImages === true && (
                        <Flickity
                            className='review-img'
                            options={{
                                autoPlay: false,
                                cellAlign: 'left',
                                contain: true,
                                draggable: isMobile,
                                fullscreen: false,
                                groupCells: '100%',
                                pageDots: true,
                                prevNextButtons: false,
                                wrapAround: false,
                            }}
                            ref={flktyHtmlRef}
                            flickityRef={(c) => {
                                setFlktyRef(c);
                            }}>
                            {images}
                        </Flickity>
                    )}
                {fullScreenActive && (
                    <ProductFullScreenHandler
                        options={{
                            infinite: false,
                            on: {
                                closing: (fancybox, slide) => {},
                            },
                            startIndex: indexZoom,
                        }}
                        items={reviewState.images.map((i) => ({
                            src: i,
                            type: 'image',
                        }))}></ProductFullScreenHandler>
                )}
                {!isNil(reviewState.answer) && (
                    <div className='prd-review-answer'>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: reviewState.answer.description,
                            }}
                        />
                        <h4 className='prd-review-answer-author'>
                            {reviewState.answer.userNickname}
                        </h4>
                    </div>
                )}
            </div>
            <div className='prd-review-footer'>
                <div className='prd-review-form'>
                    <p className='prd-review-useful'>
                        {translateService.get(`productDetail.isUseful`)}{' '}
                        <span>
                            <img
                                className='prd-review-icon'
                                onClick={handleReviewIsUseful}
                                src={
                                    reviewState.userAlreadyVotedForUseful
                                        ? common.contentFromCdn({
                                              path: `/Content/img/thumbup_b.svg`,
                                              keepUrlAsIs: false,
                                          })
                                        : common.contentFromCdn({
                                              path: `/Content/img/thumbup_w.svg`,
                                              keepUrlAsIs: false,
                                          })
                                }
                                alt={translateService.get(`productDetail.yes`)}
                            />{' '}
                            {reviewState.usefulCount}
                        </span>{' '}
                        <span>
                            <img
                                onClick={handleReviewIsUseless}
                                className='prd-review-icon'
                                src={
                                    reviewState.userAlreadyVotedForUseless
                                        ? common.contentFromCdn({
                                              path: `/Content/img/thumbdown_b.svg`,
                                              keepUrlAsIs: false,
                                          })
                                        : common.contentFromCdn({
                                              path: `/Content/img/thumbdown_w.svg`,
                                              keepUrlAsIs: false,
                                          })
                                }
                                alt={translateService.get(`productDetail.no`)}
                            />{' '}
                            {reviewState.uselessCount}
                        </span>
                    </p>
                    {isAuthenticated && (
                        <div className='prd-review-reportAbuse'>
                            <p
                                className='prd-link'
                                onClick={toggleNextElementVisibility}>
                                {translateService.get(`reviews.reportAbuse`)}
                            </p>
                            <div
                                className='prd-review-reportAbuse-form'
                                ref={reportAbuseFormRef}>
                                <p>
                                    {translateService.get(`reviews.abuseKind`)}
                                </p>
                                <label htmlFor='vulgar'>
                                    <input
                                        onMouseUp={handleRadioButtonClick}
                                        type='radio'
                                        name='vulgar'
                                        value='Vulgar'
                                    />
                                    {translateService.get(
                                        `productDetail.vulgar`
                                    )}
                                </label>
                                <label htmlFor='spam'>
                                    <input
                                        onMouseUp={handleRadioButtonClick}
                                        type='radio'
                                        name='spam'
                                        value='Spam'
                                    />
                                    {translateService.get(`productDetail.spam`)}
                                </label>
                                <label htmlFor='useless'>
                                    <input
                                        onMouseUp={handleRadioButtonClick}
                                        type='radio'
                                        name='useless'
                                        value='Useless'
                                    />
                                    {translateService.get(
                                        `productDetail.useless`
                                    )}
                                </label>
                                <p>
                                    <a
                                        className='prd-link'
                                        onClick={handleSendReportAbuse}>
                                        {translateService.get(
                                            `productDetail.send`
                                        )}
                                    </a>
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductReview;
